import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, Flag, User as UserIcon, Ruler, Mountain, Lock, BadgeCheck } from 'lucide-react';
import { FORMAT_BADGE_STYLES } from '@/lib/constants';
import type { RaceFormat } from '@/gql/graphql';

interface RaceInfoCardProps {
    creatorLabel: string;
    runnerCount: number;
    deadline?: Date | null;
    isBettingOpen: boolean;
    raceFormat?: RaceFormat | null;
    distance?: number | null;
    elevationGain?: number | null;
    startDate?: Date | null;
    isPrivate?: boolean;
    isOfficial?: boolean | null;
}

export function RaceInfoCard({ creatorLabel, runnerCount, deadline, isBettingOpen, raceFormat, distance, elevationGain, startDate, isPrivate, isOfficial }: RaceInfoCardProps) {
    const { t, i18n } = useTranslation('races');

    const [now] = useState(() => Date.now());
    const hoursLeft = deadline ? (deadline.getTime() - now) / (1000 * 60 * 60) : 0;
    const isUrgent = hoursLeft > 0 && hoursLeft < 48;

    const formatDeadlineDate = (date: Date) => {
        return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
    };

    return (
        <div className="space-y-3">
            {/* Badges row */}
            <div className="flex items-center gap-1.5 flex-wrap">
                {raceFormat && FORMAT_BADGE_STYLES[raceFormat] && (
                    <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase"
                        style={{ backgroundColor: FORMAT_BADGE_STYLES[raceFormat].bg, color: FORMAT_BADGE_STYLES[raceFormat].text }}
                    >
                        {FORMAT_BADGE_STYLES[raceFormat].label}
                    </span>
                )}
                {isOfficial && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                        <BadgeCheck size={10} />
                        {t('details.officialBadge')}
                    </span>
                )}
                {isPrivate && (
                    <span className="p-1 rounded-full bg-violet-50 text-violet-600 flex items-center">
                        <Lock size={12} />
                    </span>
                )}
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-2 text-sm text-slate-500 font-medium flex-wrap">
                <span className="flex items-center gap-1">
                    <UserIcon size={14} className="text-emerald-500" />
                    {creatorLabel}
                </span>

                <span className="text-slate-300">&bull;</span>
                <span>{runnerCount} {t('details.runners')}</span>

                {distance != null && (
                    <>
                        <span className="text-slate-300">&bull;</span>
                        <span className="flex items-center gap-1">
                            <Ruler size={14} className="text-emerald-500" />
                            {distance} km
                        </span>
                    </>
                )}

                {elevationGain != null && (
                    <>
                        <span className="text-slate-300">&bull;</span>
                        <span className="flex items-center gap-1">
                            <Mountain size={14} className="text-emerald-500" />
                            D+ {elevationGain}m
                        </span>
                    </>
                )}

                {startDate && (
                    <>
                        <span className="text-slate-300">&bull;</span>
                        <span className="flex items-center gap-1">
                            <Flag size={14} className="text-emerald-500" />
                            {new Date(startDate).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                    </>
                )}

                {isBettingOpen && deadline && (() => {
                    const sameDay = startDate && deadline.toDateString() === new Date(startDate).toDateString();
                    if (isUrgent) {
                        return (
                            <>
                                <span className="text-slate-300">&bull;</span>
                                <span className="flex items-center gap-1 text-amber-700 font-bold">
                                    <Clock size={14} className="text-amber-500" />
                                    {t('details.deadlineHours', { hours: Math.floor(hoursLeft) })}
                                </span>
                            </>
                        );
                    }
                    if (!sameDay) {
                        return (
                            <>
                                <span className="text-slate-300">&bull;</span>
                                <span className="flex items-center gap-1 text-slate-400">
                                    <Clock size={14} />
                                    {t('details.deadlineLabel')}: {formatDeadlineDate(deadline)}
                                </span>
                            </>
                        );
                    }
                    return null;
                })()}
            </div>
        </div>
    );
}
